import { useCallback, useEffect, useMemo, useState } from 'react';
import { Flex, IconButton } from '@chakra-ui/react';
import { FaTableList } from 'react-icons/fa6';
import { Link } from 'react-router-dom';

import { TableFilter, TablePagination } from '@app/components/common/table';
import { Txt } from '@app/components/common';
import Dialog from '@app/components/common/modals/dialog';
import { useAlert, useApi } from '@app/hooks';
import { Level, StructureNode } from '@app/api/models/structure';
import { usePermissions } from '@app/hooks/use-permissions';
import { ROLES } from '@app/navigation/types';
import Error404 from '@app/pages/errors/404';
import { Filters } from '@app/api';
import { TableBase } from '@app/components/common/table/table-base';

import { useTreeColumns } from './tree-columns';
import { ChainItem } from './types';
import { ResubordinateDialog } from './components/resubordinate-dialog';
import { OrgStructureForm } from './form';

export const OrgStructureViewTree = () => {
  const api = useApi();
  const { success } = useAlert();
  const [loading, setLoading] = useState(false);

  const hasPermission = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF] });
  const canChange = usePermissions({ roles: [ROLES.ADMIN, ROLES.CHIEF] });

  const [levels, setLevels] = useState<Level[]>([]);
  const [levelsMap, setLevelsMap] = useState<Map<string, number>>(new Map());
  const [chain, setChain] = useState<ChainItem[]>([]);
  const [data, setData] = useState<StructureNode[]>([]);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState<TablePagination>({
    page: 1,
    pageSize: 10,
  });

  const [selectedItem, setSelectedItem] = useState<StructureNode>();
  const [modal, setModal] = useState<'edit' | 'add' | 'remove' | 'resubordinate'>();

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data: levelsData } = await api.structure.getLevels({
        filter: {
          is_active: 'true',
        },
        sort: '-value',
        limit: 10,
        offset: 0,
      });

      const levels = levelsData.data;
      setLevelsMap(new Map(levels.map(({ id, value }) => [id.toString(), value])));
      setLevels(levels);

      const { data } = await api.structure.getNodes({
        filter: {
          level_id: levels[0].id.toString(),
          is_active: 'true',
        },
        sort: '+id',
        limit: 10,
        offset: 0,
      });

      setData(data.data);

      setLoading(false);
    })();
  }, []);

  
  const fetchData = useCallback(async ({ 
    boss_id,
    filter,
    pagination
  }: {
    boss_id?: number,
    filter: Filters,
    pagination: TablePagination,
  }) => { 
    setLoading(true);
    const { data } = await api.structure.getNodes({
      filter: { 
        ...filter,
        ...(boss_id ? { boss_id: boss_id?.toString() } : { level_id: levels[0].id.toString() } ), 
        is_active: 'true',
      },

      sort: '+id',
      limit: pagination.pageSize,
      offset: pagination.pageSize * (pagination.page - 1),
    });

    setData(data.data);
    setTotal(data.total);
    setLoading(false);
  }, [levels]);

  const tableData = useMemo(() => {
    if (chain.length === 0) {
      return data;
    }
    const sortByLevel = (a: StructureNode, b: StructureNode) => {
      const aLevel = levelsMap.get(a.level.id.toString());
      const bLevel = levelsMap.get(b.level.id.toString());
      if (aLevel === undefined || bLevel === undefined) {
        return 0;
      }
      return bLevel - aLevel;
    };

    return [
      ...chain.map(({ item }) => item),
      ...(data.length > 0 
        ? data.sort(sortByLevel)
        : [{
          id: -1,
          position: null,
          boss_id: null,
          level: levels[0],
          employee: null,
          route_number: null,
          territory: '-',
          is_active: true,
        }]
      ),
    ]; 
  }, [chain, data, levels, levelsMap]);

  const onItemClick = useCallback(async (item: StructureNode) => {
    await fetchData({
      pagination: {
        page: 1,
        pageSize: 10,
      },
      filter: {},
      boss_id: item.id,
    });
    setChain([...chain, {
      pagination,
      item,
    }]);
  }, [chain, fetchData, pagination]);

  const onMoveUpClick = useCallback(async (_item: StructureNode) => {
    const index = chain.findIndex(({ item }) => _item.id === item.id);
    if (index === 0) {
      await fetchData({
        pagination: {
          page: 1,
          pageSize: 10,
        },
        filter: {},
        boss_id: undefined,
      });
      setChain([]);
      return;
    }
    
    await fetchData({
      pagination: chain[index].pagination,
      filter: {},
      boss_id: chain[index].item.boss_id,
    });
    setPagination(chain[index].pagination);
    setChain(chain.slice(0, index));
    
  }, [chain, fetchData]);

  const onEdit = useCallback((item: StructureNode) => {
    setSelectedItem(item);
    setModal('edit');
  }, []);

  const onAdd = useCallback((item: StructureNode | null) => {
    setSelectedItem(item);
    setModal('add');
  }, []);

  const onResubordinate = useCallback((item: StructureNode) => {
    setSelectedItem(item);
    setModal('resubordinate');
  }, []);

  const columns = useTreeColumns({ chain, levels, onItemClick, onMoveUpClick, onEdit, onAdd, onResubordinate, canChange });

  const onFilterChange = useCallback((filter: TableFilter<any>[]) => {
  
  }, [fetchData]);

  const onPaginationChange = useCallback((pagination: TablePagination) => {
    fetchData({
      pagination,
      filter: {},
      boss_id: chain[chain.length - 1]?.item.id,
    });
    setPagination(pagination);
  }, [chain, fetchData]);

  const onFormSubmit = useCallback(async() => {
    
    fetchData({
      boss_id: chain[chain.length - 1]?.item.id,
      pagination,
      filter: {},
    });
    setModal(undefined);
  }, [chain, fetchData, pagination]);

  const onResubordinateSubmit = useCallback(async(instance: StructureNode) => {
    setChain([]);
    fetchData({
      boss_id: null,
      pagination: {
        page: 1,
        pageSize: 10
      },
      filter: {},
    });
    setModal(undefined);
  }, [fetchData]);

  const onDownload = useCallback(async () => {
    const { code, message } = await api.structure.getNodes({
      filter: {},
      limit: 100,
      offset: 0,
      sort: '+id',
      download: '1'
    });

    if (code === 200) {
      success(message);
    }
  }, [api]);
  
  if (!hasPermission) {return <Error404/>;}

  return <>
    <TableBase<StructureNode>
      title={<Flex gap={2} alignItems='center'>
        <Txt>Структура организации</Txt>
        <Link to="/org-structure/table/" aria-label="table-view" >
          <IconButton aria-label="table-view" icon={<FaTableList color='gray.500' />} />
        </Link>
      </Flex>}
      columns={columns}
      data={tableData}
      loading={loading}
      onFilterChange={onFilterChange}   
      onCreateClick={canChange ? () => onAdd(null) : undefined}
      total={total}
      pagination={pagination}
      onPaginationChange={onPaginationChange}
      onDownload={onDownload}
    />
    <Dialog open={modal === 'edit' || modal === 'add'}
      onClose={() => setModal(undefined)}
      title={modal === 'edit' ? 'Редактирование' : 'Добавление'}
      width={800}
    >
      <OrgStructureForm
        parent={modal === 'add' ? selectedItem : chain.find( ({ item }) => item.id === selectedItem?.boss_id)?.item}
        initial={modal === 'edit' ? selectedItem : undefined}
        onSubmit={onFormSubmit}
        onCancel={() => setModal(undefined)}
        levels={levels}
      />
    </Dialog>
    <Dialog open={modal === 'resubordinate'}
      onClose={() => setModal(undefined)}
      title="Переподчинение"
      width={800}
    >
      <ResubordinateDialog 
        levels={levels}
        parent={modal === 'add' ? selectedItem : chain.find( ({ item }) => item.id === selectedItem?.boss_id)?.item}
        node={selectedItem}
        onSubmit={onResubordinateSubmit}
        onClose={() => setModal(undefined)}
      />
    </Dialog>
  </>;
};